import React, { useState, useRef, useEffect } from 'react'
import { Search, X, Coins, Image, Server, MapPin, History, Clock, Wallet, Globe, Code } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { configService } from '@/config'
import { log } from '@/utils/logger'
import { useSearchHistory } from '@/hooks/useSearchHistory'
import { detectSearchType as detectSearchTypeUtil } from '@/utils/searchDetection'
import { getAccountType } from '@/utils/rpcHelpers'
import { TokenMetadataService } from '@/services/tokenMetadataService'
import { formatValidatorLabel } from '@/utils/validatorName'
import { ValidatorNameBadge } from '@/components/common/ValidatorNameBadge'
import centralizedValidatorService from '@/services/centralizedValidatorService'
import { useQuery } from '@tanstack/react-query'

// Typewriter animation words (with ... included so it gets backspaced too)
const TYPEWRITER_WORDS = [
  'Validators...',
  'Tokens...',
  'NFTs...',
  'Transactions...',
  'Addresses...',
  'Blocks...',
]

// Custom hook for typewriter effect
const useTypewriter = (words: string[], typingSpeed = 100, deletingSpeed = 50, pauseDuration = 2000) => {
  const [displayText, setDisplayText] = useState('')
  const [wordIndex, setWordIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showCursor, setShowCursor] = useState(true)

  // Blinking cursor effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev)
    }, 530)
    return () => clearInterval(cursorInterval)
  }, [])

  // Typing/deleting effect
  useEffect(() => {
    const currentWord = words[wordIndex]

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1))
        } else {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), pauseDuration)
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1))
        } else {
          setIsDeleting(false)
          setWordIndex((prev) => (prev + 1) % words.length)
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed)

    return () => clearTimeout(timeout)
  }, [displayText, isDeleting, wordIndex, words, typingSpeed, deletingSpeed, pauseDuration])

  return { displayText, showCursor }
}

interface SearchResultCategory {
  type: 'validator_info' | 'validator_location' | 'tokens' | 'nfts' | 'x1ns_domains' | 'programs'
  label: string
  icon: React.ReactNode
  results: any[]
  getTitle: (item: any) => string
  getSubtitle: (item: any) => string
  getHref: (item: any, query: string) => string
  getIcon?: (item: any) => string | null
}

interface GlobalSearchProps {
  className?: string
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ className = '' }) => {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  // Typewriter animation
  const { displayText, showCursor } = useTypewriter(TYPEWRITER_WORDS)
  const [categories, setCategories] = useState<SearchResultCategory[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0)
  const [selectedResultIndex, setSelectedResultIndex] = useState(0)
  const [detectedType, setDetectedType] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [tokenImages, setTokenImages] = useState<Map<string, string>>(new Map())

  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Get current network - Network aware!
  const currentNetwork = configService.getCurrentNetwork()

  // Search history hook
  const { getRecentSearches, addToHistory, removeFromHistory } = useSearchHistory()

  // Lane V.2 (WP #4084): the global search backend (Meilisearch) doesn't yet
  // carry the `nameProvenance` field. Cross-reference each validator_info
  // result with the cached validators list (same React Query key the dashboard
  // + /validators page share) so the badge tier matches the rest of the UI.
  const { data: cachedValidatorList } = useQuery({
    queryKey: ['validators', currentNetwork],
    queryFn: () => centralizedValidatorService.getAllValidators(),
    staleTime: 3 * 60 * 1000,
  })
  const provenanceByPubkey = React.useMemo(() => {
    const m = new Map<string, 'config' | 'keybase' | 'proxy' | 'fallback' | null>()
    ;(cachedValidatorList || []).forEach((v: any) => {
      if (!v.pubkey) return
      const p = v.nameProvenance
      m.set(
        v.pubkey,
        p === 'config' || p === 'keybase' || p === 'proxy' || p === 'fallback' ? p : null
      )
    })
    return m
  }, [cachedValidatorList])

  // Helper function to automatically detect search type (no prefix required)
  const detectSearchType = (query: string): {
    type: 'address' | 'transaction' | 'block' | 'epoch' | 'validator' | 'token' | 'program' | 'domain' | null,
    value: string,
    syntax?: string
  } => {
    const trimmed = query.trim()

    // 1. Check if it's a Solana Name Service domain (.sol)
    if (trimmed.endsWith('.sol')) {
      return { type: 'domain', value: trimmed }
    }

    // 2. Check if it's a block/slot/epoch number (all digits)
    if (/^\d+$/.test(trimmed)) {
      const num = parseInt(trimmed)
      // Epoch numbers are typically < 1000, slots are much larger
      if (num < 1000) {
        return { type: 'epoch', value: trimmed }
      }
      return { type: 'block', value: trimmed }
    }

    // 3. Check if it's a base58 string (Solana addresses/hashes/signatures)
    // Base58 alphabet: 123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz (no 0, O, I, l)
    if (/^[1-9A-HJ-NP-Za-km-z]+$/.test(trimmed)) {
      // Transaction signatures are typically 87-88 characters
      if (trimmed.length >= 87 && trimmed.length <= 88) {
        return { type: 'transaction', value: trimmed }
      }
      // Addresses (pubkeys, mint addresses, program IDs) are typically 32-44 characters
      else if (trimmed.length >= 32 && trimmed.length <= 44) {
        return { type: 'address', value: trimmed }
      }
    }

    // 4. Everything else: search by name/symbol in Meilisearch
    // This includes: validator names, token symbols, token names, NFT names, etc.
    return { type: null, value: trimmed }
  }

  // Meilisearch-powered search function - searches only current network schema
  const performSearch = async (searchQuery: string): Promise<SearchResultCategory[]> => {
    if (!searchQuery.trim()) return []

    setIsLoading(true)

    try {
      // Check if the query looks like an address, transaction hash, or block number
      const searchType = detectSearchType(searchQuery)

      // First, check Meilisearch for any address matches (tokens, NFTs, validators)
      // This allows database-first searching for addresses before falling back to RPC
      // Use the detected value for syntax queries (symbol:USDC -> search "USDC")
      const searchValue = searchType.value || searchQuery
      const response = await fetch(
        `/api/search?q=${encodeURIComponent(searchValue)}&network=${currentNetwork}&limit=5`
      )

      if (!response.ok) {
        throw new Error('Search failed')
      }

      const data = await response.json()
      const categories: SearchResultCategory[] = []

      // Store detected type from backend
      if (data.typeDescription) {
        setDetectedType(data.typeDescription)
      }

      // Check if we found any results in Meilisearch
      const hasResults = Object.values(data.results).some((arr: any) => arr.length > 0)

      // For blocks and epochs with no results, navigate directly (simple lookups)
      // For addresses and transactions, always show dropdown so user can choose
      // and the destination pages will use historical RPC pool for full data
      if (searchType.type && !hasResults) {
        // Only auto-navigate for blocks and epochs (simple numeric lookups)
        if (searchType.type === 'block' || searchType.type === 'epoch') {
          setIsLoading(false)
          const path = searchType.type === 'block'
            ? `/block/${searchType.value}`
            : `/epoch/${searchType.value}`
          navigate(path)
          setIsOpen(false)
          setQuery('')
          return []
        }
        // For addresses, transactions, tokens - fall through to show dropdown
        // This ensures the historical RPC pool is used when viewing the page
      }

      // Validator Info - search by name, description, website
      if (data.results.validator_info && data.results.validator_info.length > 0) {
        categories.push({
          type: 'validator_info',
          label: 'Validators',
          icon: <Server className="h-4 w-4 text-emerald-500" />,
          results: data.results.validator_info,
          // Lane V.2 (WP #4084): no synthesized "Validator <slice>" placeholder.
          // When v.name is null/empty, return the empty string — the dropdown
          // result-row renders ValidatorNameBadge for validator_info, which
          // renders a truncated pubkey + Unverified badge as the fallback.
          getTitle: (v) =>
            typeof v.name === 'string' && v.name.length > 0 ? v.name : '',
          getSubtitle: (v) => v.website || v.description?.slice(0, 60) || 'Validator',
          getHref: (v, q) => `/validators/${v.validator_identity}`,
          getIcon: (v) => v.icon_url || null,
        })
      }

      // Validator Locations - search by country, city, ISP, organization
      if (data.results.validator_location && data.results.validator_location.length > 0) {
        categories.push({
          type: 'validator_location',
          label: 'Validator Locations',
          icon: <MapPin className="h-4 w-4 text-blue-500" />,
          results: data.results.validator_location,
          getTitle: (v) => `${v.city_name || 'Unknown'}, ${v.country_name || 'Unknown'}`,
          getSubtitle: (v) => `${v.isp || 'ISP'} • ${v.organization || 'Network'}`,
          getHref: (v, q) => `/validator-map?location=${encodeURIComponent(q)}`,
        })
      }

      // Tokens - search by name, symbol, mint address
      if (data.results.tokens && data.results.tokens.length > 0) {
        categories.push({
          type: 'tokens',
          label: 'Tokens',
          icon: <Coins className="h-4 w-4 text-yellow-500" />,
          results: data.results.tokens,
          getTitle: (t) => `${t.name || 'Unknown'} ${t.symbol ? `(${t.symbol})` : ''}`,
          getSubtitle: (t) => `${t.mint_address_short || t.mint_address?.slice(0, 16)} • ${t.decimals || 9} decimals`,
          getHref: (t, q) => `/token/${t.mint_address}`,
          getIcon: (t) => t.logo_uri || t.image || null,
        })
      }

      // NFTs - search by name, mint address
      if (data.results.nfts && data.results.nfts.length > 0) {
        categories.push({
          type: 'nfts',
          label: 'NFTs',
          icon: <Image className="h-4 w-4 text-pink-500" />,
          results: data.results.nfts,
          getTitle: (n) => n.name || `NFT ${n.mint_address?.slice(0, 8)}`,
          getSubtitle: (n) => `${n.collection_name || 'No collection'} ${n.is_verified ? '✓' : ''}`,
          getHref: (n, q) => `/nft/${n.mint_address}`,
          getIcon: (n) => n.image || n.image_uri || null,
        })
      }

      // Programs - search by friendly name, program id, category (CH program_labels)
      if (data.results.programs && data.results.programs.length > 0) {
        categories.push({
          type: 'programs',
          label: 'Programs',
          icon: <Code className="h-4 w-4 text-blue-500" />,
          results: data.results.programs,
          getTitle: (p) => p.name || `Program ${p.program_id?.slice(0, 8)}`,
          getSubtitle: (p) => `${p.program_id_short || p.program_id?.slice(0, 16)} • ${p.category || 'Program'}${p.verified ? ' ✓' : ''}`,
          getHref: (p) => `/program/${p.program_id}`,
          getIcon: () => null,
        })
      }

      // X1NS Domains - search by domain name
      if (data.results.x1ns_domains && data.results.x1ns_domains.length > 0) {
        categories.push({
          type: 'x1ns_domains',
          label: 'X1NS Domains',
          icon: <Globe className="h-4 w-4 text-teal-500" />,
          results: data.results.x1ns_domains,
          getTitle: (d) => d.domain || d.domain_name || 'Unknown Domain',
          getSubtitle: (d) => `Owner: ${d.owner_short || d.owner?.slice(0, 8) + '...' + d.owner?.slice(-6) || 'Unknown'}`,
          getHref: (d, q) => `/account/${d.owner}`,
          getIcon: () => null, // X1NS domains don't have icons yet
        })
      }

      // ALWAYS add "View as Account" option when query looks like a valid address
      // This allows users to view any pubkey as an account, even if it's also a validator/token
      if (searchType.type === 'address' && searchType.value) {
        categories.push({
          type: 'validator_info' as const, // Reuse type for compatibility
          label: 'Account',
          icon: <Wallet className="h-4 w-4 text-cyan-500" />,
          results: [{
            address: searchType.value,
            type: 'account'
          }],
          getTitle: () => `View as Account`,
          getSubtitle: () => `${searchType.value.slice(0, 8)}...${searchType.value.slice(-8)} • Address details`,
          getHref: () => `/account/${searchType.value}`,
        })

        // Also add "View as Program" option for any address
        categories.push({
          type: 'validator_info' as const,
          label: 'Program',
          icon: <Code className="h-4 w-4 text-orange-500" />,
          results: [{
            address: searchType.value,
            type: 'program'
          }],
          getTitle: () => `View as Program`,
          getSubtitle: () => `${searchType.value.slice(0, 8)}...${searchType.value.slice(-8)} • Program details & analytics`,
          getHref: () => `/program/${searchType.value}`,
        })
      }

      // Also add "View as Transaction" option when query looks like a signature
      if (searchType.type === 'transaction' && searchType.value) {
        categories.push({
          type: 'validator_info' as const,
          label: 'Transaction',
          icon: <History className="h-4 w-4 text-indigo-500" />,
          results: [{
            signature: searchType.value,
            type: 'transaction'
          }],
          getTitle: () => `View Transaction`,
          getSubtitle: () => `${searchType.value.slice(0, 8)}...${searchType.value.slice(-8)} • Transaction details`,
          getHref: () => `/tx/${searchType.value}`,
        })
      }

      // L4: honor the backend's exact-match hint — move that category to the
      // front WITHOUT dropping any others. Fixes "XNT" surfacing the validator
      // above the XNT token; validators are still shown, just below the token.
      if (data.topCategory) {
        const idx = categories.findIndex((c) => c.type === data.topCategory)
        if (idx > 0) {
          const [top] = categories.splice(idx, 1)
          categories.unshift(top)
        }
      }

      setIsLoading(false)
      return categories

    } catch (error) {
      log.error('Meilisearch search error:', error)
      setIsLoading(false)
      return []
    }
  }

  // Handle search input changes with debounce
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (query.trim()) {
        const searchCategories = await performSearch(query)
        setCategories(searchCategories)
        setSelectedCategoryIndex(0)
      } else {
        setCategories([])
      }
    }, 400) // 400ms debounce

    return () => clearTimeout(timeoutId)
  }, [query, currentNetwork]) // Re-search when network changes

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard shortcuts and navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Global shortcut: Cmd/Ctrl + K to focus search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
        setIsOpen(true)
      }

      // Only handle these keys when search is open
      if (!isOpen) return

      // Escape to close
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsOpen(false)
        setShowHistory(false)
        inputRef.current?.blur()
      }

      // Enter to select current result
      if (event.key === 'Enter' && categories.length > 0) {
        event.preventDefault()
        const selectedCategory = categories[selectedCategoryIndex]
        const selectedResult = selectedCategory?.results[selectedResultIndex]
        if (selectedCategory && selectedResult) {
          handleResultClick(selectedCategory, selectedResult)
        }
      }

      // Arrow Down - navigate to next result
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        const currentCategory = categories[selectedCategoryIndex]
        if (!currentCategory) return

        if (selectedResultIndex < currentCategory.results.length - 1) {
          // Move to next result in same category
          setSelectedResultIndex(prev => prev + 1)
        } else if (selectedCategoryIndex < categories.length - 1) {
          // Move to next category
          setSelectedCategoryIndex(prev => prev + 1)
          setSelectedResultIndex(0)
        }
      }

      // Arrow Up - navigate to previous result
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (selectedResultIndex > 0) {
          // Move to previous result in same category
          setSelectedResultIndex(prev => prev - 1)
        } else if (selectedCategoryIndex > 0) {
          // Move to previous category (last result)
          const prevCategory = categories[selectedCategoryIndex - 1]
          setSelectedCategoryIndex(prev => prev - 1)
          setSelectedResultIndex(prevCategory.results.length - 1)
        }
      }

      // Tab to cycle through categories
      if (event.key === 'Tab' && categories.length > 0) {
        event.preventDefault()
        setSelectedCategoryIndex((prev) => (prev + 1) % categories.length)
        setSelectedResultIndex(0)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, categories, selectedCategoryIndex, selectedResultIndex])

  const handleResultClick = (category: SearchResultCategory, result: any) => {
    const href = category.getHref(result, query)

    // Add to search history
    addToHistory(query, category.type)

    navigate(href)
    setIsOpen(false)
    setShowHistory(false)
    setQuery('')
  }

  const handleClearSearch = () => {
    setQuery('')
    setCategories([])
    inputRef.current?.focus()
  }

  const handleCategoryClick = (category: SearchResultCategory) => {
    // Navigate to the category's filtered page with search query
    let path = ''

    switch (category.type) {
      case 'validator_info':
      case 'validator_location':
        // Go to validators list page with search filter
        path = `/validators?search=${encodeURIComponent(query)}`
        break
      case 'tokens':
        // Go to tokens page (if you have one) or first result
        path = `/tokens?search=${encodeURIComponent(query)}`
        break
      case 'nfts':
        // Go to NFTs page (if you have one) or first result
        path = `/nfts?search=${encodeURIComponent(query)}`
        break
      case 'x1ns_domains':
        // For X1NS domains, navigate to the first result's owner account
        const firstDomain = category.results[0]
        path = firstDomain?.owner ? `/account/${firstDomain.owner}` : `/search?q=${encodeURIComponent(query)}`
        break
      default:
        // Fallback to first result if no list page exists
        const firstResult = category.results[0]
        path = category.getHref(firstResult, query)
    }

    navigate(path)
    setIsOpen(false)
    setQuery('')
  }

  const getTotalResults = () => {
    return categories.reduce((sum, cat) => sum + cat.results.length, 0)
  }

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setShowHistory(false) // Hide history when typing
            setSelectedCategoryIndex(0)
            setSelectedResultIndex(0)
          }}
          onFocus={() => {
            setIsFocused(true)
            setIsOpen(true)
            if (!query.trim()) {
              setShowHistory(true) // Show history when focusing empty input
            }
          }}
          onBlur={() => setIsFocused(false)}
          placeholder=""
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-pill bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {/* Typewriter animated placeholder */}
        {!query && !isFocused && (
          <div className="absolute inset-y-0 left-0 pl-10 flex items-center pointer-events-none">
            <span className="text-gray-500" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
              Search for {displayText}
              <span
                className={`inline-block w-0.5 h-4 bg-gray-500 ml-0.5 align-middle ${showCursor ? 'opacity-100' : 'opacity-0'}`}
                style={{ transition: 'opacity 0.1s' }}
              />
            </span>
          </div>
        )}
        {/* Show static placeholder when focused but empty */}
        {!query && isFocused && (
          <div className="absolute inset-y-0 left-0 pl-10 flex items-center pointer-events-none">
            <span className="text-gray-500" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
              Search for... <span className="text-gray-400 text-xs">(Ctrl+K)</span>
            </span>
          </div>
        )}
        {/* Simple tooltip on hover */}
        <div className="absolute left-0 -bottom-2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-pill py-2 px-3 shadow-lg whitespace-nowrap">
            <p className="text-gray-200">Search anything... we'll figure it out</p>
          </div>
        </div>
        {query && (
          <button
            onClick={handleClearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-[600px] overflow-y-auto">
          {/* Search History */}
          {showHistory && !query && getRecentSearches().length > 0 && (
            <div className="py-2">
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <History className="h-4 w-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Recent Searches ({currentNetwork})
                  </span>
                </div>
              </div>
              {getRecentSearches().map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setQuery(item.query)
                    setShowHistory(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between group"
                >
                  <div className="flex items-center space-x-3">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.query}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.type} • {new Date(item.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFromHistory(item.query)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <X className="h-3 w-3 text-gray-500" />
                  </button>
                </button>
              ))}
            </div>
          )}

          {isLoading && (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
              <span className="mt-2 block text-sm">Searching {currentNetwork}...</span>
            </div>
          )}

          {!isLoading && query && categories.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">No results found for "{query}" on {currentNetwork}</p>
              <p className="text-xs mt-1">Try searching for validators, tokens, NFTs, or locations</p>
            </div>
          )}

          {!isLoading && categories.length > 0 && (
            <div className="py-2">
              {/* Summary Header */}
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {getTotalResults()} results in {categories.length} categories ({currentNetwork})
                  </p>
                  {detectedType && (
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                      {detectedType}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Click a category to see all results • Tab to switch categories
                </p>
              </div>

              {/* Categories */}
              {categories.map((category, catIndex) => (
                <div key={category.type} className={`${catIndex !== 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}>
                  {/* Category Header - Clickable */}
                  <button
                    onClick={() => handleCategoryClick(category)}
                    className={`w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedCategoryIndex === catIndex ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {category.icon}
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        {category.label}
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                        {category.results.length}
                      </span>
                    </div>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      View all →
                    </span>
                  </button>

                  {/* Category Results */}
                  {category.results.slice(0, 3).map((result, index) => {
                    const iconUrl = category.getIcon?.(result)
                    const isSelected = selectedCategoryIndex === catIndex && selectedResultIndex === index
                    return (
                      <button
                        key={index}
                        onClick={() => handleResultClick(category, result)}
                        className={`w-full px-3 py-2 pl-9 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-3 border-l-2 transition-all ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400'
                            : 'border-transparent hover:border-blue-500 dark:hover:border-blue-400'
                        }`}
                      >
                        {/* Icon/Image - show image if available, otherwise show category icon */}
                        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => {
                                // On error, hide the image (fallback icon will show)
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : (
                            // Fallback: show category icon
                            <span className="scale-110">{category.icon}</span>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Lane V.2 (WP #4084): validator_info rows render the
                              verified / inferred / unverified badge by cross-
                              referencing the cached validator list (Meilisearch
                              hits don't carry nameProvenance yet). Everything
                              else uses the plain title text. */}
                          {category.type === 'validator_info' && result.validator_identity ? (
                            <ValidatorNameBadge
                              validator={{
                                pubkey: result.validator_identity,
                                name: typeof result.name === 'string' && result.name.length > 0 ? result.name : null,
                                nameProvenance: provenanceByPubkey.get(result.validator_identity) ?? null,
                              }}
                              className="text-sm font-medium"
                              size="sm"
                            />
                          ) : (
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {category.getTitle(result)}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {category.getSubtitle(result)}
                          </p>
                        </div>
                      </button>
                    )
                  })}

                  {/* Show more indicator */}
                  {category.results.length > 3 && (
                    <button
                      onClick={() => handleCategoryClick(category)}
                      className="w-full px-3 py-1.5 pl-9 text-left text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      +{category.results.length - 3} more {category.label.toLowerCase()}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!query && (
            <div className="p-4 text-center text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">Start typing to search on {currentNetwork}</p>
              <p className="text-xs mt-1">Powered by FortiBlox</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
